import {
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { isEqual } from 'lodash-es';
import { firstValueFrom, map } from 'rxjs';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';
import { VISIT_COST_OPTION } from '@features/patient-group-dropdown/constants/visit-cost-option.const';
import { PatientGroupDropdownComponent } from '@features/patient-group-dropdown/patient-group-dropdown.component';
import { ProtocolVersionDropdownComponent } from '@features/protocol-version-dropdown/protocol-version-dropdown.component';
import { SiteBudgetCostTypeDropdownComponent } from '@features/site-budget-cost-type-dropdown/site-budget-cost-type-dropdown.component';

import { injectPatientGroups } from '@models/patient-protocol/patient-protocol.api';
import {
  injectProcessSiteBudgetEffectiveDateChangeMutation,
  injectProcessUpdateInvestigatorTransactionMutation,
  injectSiteBudget,
  injectUpdateSiteBudgetMutation,
} from '@models/sites/api/site-budget.api';
import {
  injectBatchCreateSiteInvoiceables,
  injectBatchUpdateSiteInvoiceables,
} from '@models/sites/api/site-invoiceables.api';
import {
  injectSitePaymentSchedulesBySiteId,
  injectSiteProtocolVersions,
} from '@models/sites/api/sites.api';
import { SiteCostType } from '@models/sites/site-cost.enum';
import { SitesQuery } from '@models/sites/sites.query';
import { SiteModel } from '@models/sites/sites.store';

import {
  CreateSitePaymentScheduleInput,
  listPatientGroupsQuery,
  listSiteBudgetVersionsQuery,
  PatientGroupType,
  UpdateSitePaymentScheduleInput,
} from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import {
  injectCreateSitePaymentScheduleMutation,
  injectUpdateSitePaymentScheduleMutation,
} from '@shared/store/payment-schedules/api/payment-schedules.api';
import { Utils } from '@shared/utils/utils';

import { ChangeEffectiveDateModalComponent } from './components/change-effective-date-modal/change-effective-date-modal.component';
import { EffectiveDateDropdownComponent } from './components/effective-date-dropdown/effective-date-dropdown.component';
import { NoProtocolVersionsLinkComponent } from './components/no-protocol-versions-link/no-protocol-versions-link.component';
import { SiteAmendmentButtonComponent } from './components/site-amendment-button/site-amendment-button.component';
import { SiteBudgetDropdownComponent } from './components/site-budget-dropdown/site-budget-dropdown.component';
import { SiteBudgetGridComponent } from './components/site-budget-grid/site-budget-grid.component';
import {
  GridRowData,
  SiteInvoiceableRowData,
} from './components/site-budget-grid/types/site-budget-grid-row';
import {
  convertToCreateSiteInvoiceableInput,
  convertToUpdateSiteInvoiceableInput,
} from './components/site-budget-grid/utils/site-invoiceables-grid.utils';
import { SiteBudgetNoteComponent } from './components/site-budget-note/site-budget-note.component';
import { SiteBudgetSupportingDocumentsComponent } from './components/site-budget-supporting-documents/site-budget-supporting-documents.components';
import { SiteBudgetTableButtonsComponent } from './components/site-budget-table-buttons/site-budget-table-buttons.component';
import { EDITABLE_SITE_BUDGET_FORM_FIELDS } from './constants/editable-site-budget-form-fields.const';
import { SiteBudgetForm } from './types/site-budget-form.type';

@Component({
  hostDirectives: [SiteWorkflowDirective],
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    SiteBudgetGridComponent,
    EffectiveDateDropdownComponent,
    SiteBudgetTableButtonsComponent,
    SiteAmendmentButtonComponent,
    SiteBudgetNoteComponent,
    SiteBudgetSupportingDocumentsComponent,
    SiteBudgetDropdownComponent,
    NoProtocolVersionsLinkComponent,
    SiteBudgetCostTypeDropdownComponent,
    PatientGroupDropdownComponent,
    ProtocolVersionDropdownComponent,
  ],
  selector: 'aux-site-budget',
  templateUrl: './site-budget.component.html',
})
export class SiteBudgetComponent {
  route = inject(ActivatedRoute);
  router = inject(Router);
  siteWorkflow = inject(SiteWorkflowDirective);

  private fb = inject(FormBuilder);
  private overlayService = inject(OverlayService);
  private siteQuery = inject(SitesQuery);

  createSitePayment = injectCreateSitePaymentScheduleMutation();
  flags = injectFlags({ visitCosts: 'visit_costs' });
  injectBatchCreateSiteInvoiceables = injectBatchCreateSiteInvoiceables();
  injectBatchUpdateSiteInvoiceables = injectBatchUpdateSiteInvoiceables();
  injectSitePatientGroups = injectPatientGroups({
    patientGroupTypes: [PatientGroupType.PATIENT_GROUP_STANDARD],
  });
  processSiteBudgetEffectiveDateChangeMutation =
    injectProcessSiteBudgetEffectiveDateChangeMutation();
  processUpdateInvestigatorTransactionMutation =
    injectProcessUpdateInvestigatorTransactionMutation();
  protocolVersions = injectSiteProtocolVersions();

  selectedSiteId$ = this.siteQuery
    .selectActiveId()
    .pipe(map((id) => (id?.length ? (id[0] as string) : null)));

  selectedSiteId = toSignal(this.selectedSiteId$);

  siteBudget = injectSiteBudget({ site_id: this.selectedSiteId });

  selectedCostType = signal<SiteCostType>(SiteCostType.PATIENT_VISITS);
  selectedProtocolVersion = signal<null | string>(null);

  sitePaymentSchedules = injectSitePaymentSchedulesBySiteId({
    costType: this.selectedCostType,
    protocolVersion: this.selectedProtocolVersion,
    siteId: this.selectedSiteId,
  });
  updateSiteBudgetMutation = injectUpdateSiteBudgetMutation(true);
  updateSitePayment = injectUpdateSitePaymentScheduleMutation();

  editMode = signal(false);
  isDataProcessing = signal(false);
  selectedBudgetVersion = signal<null | string>(null);
  selectedPatientGroup = signal<null | string>(null);

  selectedSite$ = this.siteQuery.selectActive().pipe(map(([site]) => site));

  selectedSite = toSignal(this.selectedSite$) as WritableSignal<SiteModel>;

  editableControls = computed(() => {
    const isPatientVisitsSelected = this.selectedCostType() === SiteCostType.PATIENT_VISITS;

    if (!isPatientVisitsSelected) {
      return EDITABLE_SITE_BUDGET_FORM_FIELDS.filter((field) => field !== 'patientGroup');
    }

    return EDITABLE_SITE_BUDGET_FORM_FIELDS;
  });
  isAllDataLoaded = computed(() => {
    return (
      this.protocolVersions.isSuccess() &&
      this.siteBudget.isSuccess() &&
      this.injectSitePatientGroups.isSuccess()
    );
  });
  isSiteInvoiceablesSelected = computed(
    () => this.selectedCostType() === SiteCostType.SITE_INVOICEABLES
  );
  isVisitCostSelected = computed(() => {
    return this.selectedCostType() === SiteCostType.PATIENT_VISITS;
  });

  effectiveDateDropdown = viewChild<EffectiveDateDropdownComponent>('effectiveDateDropdown');
  siteBudgetGrid = viewChild<SiteBudgetGridComponent>('siteBudgetGrid');

  initialDataState!: SiteBudgetForm;
  siteBudgetForm = this.fb.group<SiteBudgetForm>({
    costType: SiteCostType.PATIENT_VISITS,
    effectiveDate: null,
    note: null,
    noteId: null,
    patientGroup: null,
    protocolVersion: null,
    siteBudgetName: null,
    siteBudgetVersion: null,
  });

  cancelChanges() {
    this.editMode.set(false);
    this.siteBudgetGrid()?.cancelChanges();
    this.siteBudgetGrid()?.setMostRecentSiteInvoiceablesColumnState();
    this.siteBudgetForm.patchValue({
      effectiveDate: this.initialDataState.effectiveDate,
      note: this.initialDataState.note,
      noteId: this.initialDataState.noteId,
    });
  }

  async canDeactivate(): Promise<boolean> {
    const isNoteChanged = this.siteBudgetForm.getRawValue().note !== this.initialDataState?.note;

    if (this.isFormChanged() && isNoteChanged && this.editMode()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  async confirmEffectiveDateChange(): Promise<boolean> {
    if (this.initialDataState?.effectiveDate !== this.siteBudgetForm.getRawValue().effectiveDate) {
      const modalEvent = await firstValueFrom(
        this.overlayService.open<{ success: boolean }>({
          baseComponent: ChangeEffectiveDateModalComponent,
        }).afterClosed$
      );

      if (!modalEvent?.data?.success) return false;
    }

    return true;
  }

  constructor() {
    this.siteBudgetForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      const { costType, patientGroup, protocolVersion, siteBudgetVersion } =
        this.siteBudgetForm.getRawValue();

      this.selectedProtocolVersion.set(protocolVersion ?? null);
      this.selectedBudgetVersion.set(siteBudgetVersion ?? null);
      this.selectedPatientGroup.set(patientGroup ?? null);
      this.selectedCostType.set(costType ?? SiteCostType.PATIENT_VISITS);
    });

    effect(() => {
      if (this.isAllDataLoaded()) {
        const manualProtocolVersion = this.selectedProtocolVersion()
          ? { protocolVersionId: this.selectedProtocolVersion()! }
          : undefined;

        this.setInitialValues(manualProtocolVersion)
          .then(() => {
            this.initialDataState = this.siteBudgetForm.getRawValue();
          })
          .catch(console.error);
      }
    });

    effect(() => {
      if (
        this.selectedProtocolVersion() &&
        this.selectedBudgetVersion() &&
        this.selectedCostType()
      ) {
        void this.setInitialValues({
          protocolVersionId: this.selectedProtocolVersion()!,
          siteBudgetVersionId: this.selectedBudgetVersion()!,
        })
          .then(() => {
            this.initialDataState = this.siteBudgetForm.getRawValue();
          })
          .catch(console.error);
      }
    });

    effect(() => {
      const patientGroupControl = this.siteBudgetForm.controls.patientGroup;

      if (this.isVisitCostSelected()) {
        patientGroupControl.enable();
      } else {
        patientGroupControl.disable();
      }
    });

    effect(() => {
      this.editableControls().forEach((field) => {
        const control = this.siteBudgetForm.controls[field];

        if (!control) return;

        if (this.editMode()) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      });
    });
  }

  private getInitialPatientGroup(patientGroups: listPatientGroupsQuery[]): null | string {
    if (!this.isVisitCostSelected()) {
      return ' ';
    }

    const isVisitCostEnabled = this.flags().visitCosts;

    if (patientGroups.length) {
      return patientGroups[0].id;
    }

    return isVisitCostEnabled ? VISIT_COST_OPTION.value : null;
  }

  goToEditMode() {
    this.editMode.set(true);

    setTimeout(() => {
      this.siteBudgetGrid()?.setFirstEditableCell();
    }, 0);
  }

  isFormChanged() {
    const siteBudgetGrid = this.siteBudgetGrid();

    const gridData = siteBudgetGrid?.gridData() || [];
    const initialGridData = siteBudgetGrid?.initialData || [];

    const isGridDataChanged = !isEqual(gridData, initialGridData);

    const isFieldsChanges = !isEqual(this.siteBudgetForm.getRawValue(), this.initialDataState);

    return isGridDataChanged || isFieldsChanges;
  }

  isInitialSiteBudget(): boolean {
    const siteBudgetData = this.siteBudget.data() || [];
    const firstProtocolVersionId = this.selectedSite()?.starting_protocol_version_id ?? null;

    return siteBudgetData.some(
      (budget) =>
        budget.id === this.selectedBudgetVersion() &&
        budget.is_baseline &&
        budget.patient_protocol_version_id === firstProtocolVersionId
    );
  }

  async onAmendmentCreated(siteBudget: listSiteBudgetVersionsQuery) {
    await this.siteBudget.refetch();
    await this.siteBudgetGrid()?.sitePaymentSchedules.refetch();

    const note = siteBudget?.notes?.length ? siteBudget?.notes[0] : null;

    this.siteBudgetForm.patchValue({
      note: note?.message ?? null,
      noteId: note?.id ?? null,
      protocolVersion: siteBudget.patient_protocol_version_id,
      siteBudgetName: siteBudget.name,
      siteBudgetVersion: siteBudget.id,
    });
  }

  onRemoveRows() {
    this.siteBudgetGrid()?.setMostRecentSiteInvoiceablesColumnState();

    this.editMode.set(false);
  }

  async saveChanges() {
    const { effectiveDate, protocolVersion, siteBudgetName, siteBudgetVersion } =
      this.siteBudgetForm.getRawValue();

    if (this.siteBudgetForm.invalid || !siteBudgetVersion) {
      return;
    }

    if (!(await this.confirmEffectiveDateChange())) {
      return;
    }

    if (!this.siteBudgetGrid()?.validateGridData(true)) {
      return;
    }

    this.isDataProcessing.set(true);

    try {
      await this.updateSiteBudgetMutation.mutateAsync({
        effective_date: effectiveDate,
        id: siteBudgetVersion,
        name: siteBudgetName,
        site_id: this.selectedSiteId() ?? '',
      });

      if (siteBudgetVersion && this.initialDataState.effectiveDate !== effectiveDate) {
        await this.processSiteBudgetEffectiveDateChangeMutation.mutateAsync({
          effectiveDate: effectiveDate ?? '',
          prevEffectiveDate: this.initialDataState.effectiveDate ?? '',
          protocolVersion: protocolVersion!,
          siteBudgetName: siteBudgetName!,
          siteBudgetVersionId: siteBudgetVersion!,
        });
      }

      await this.saveUpdateGridData();

      await this.processUpdateInvestigatorTransactionMutation.mutateAsync({
        siteId: this.selectedSiteId() ?? '',
      });

      this.overlayService.success('Successfully saved');

      this.siteBudgetGrid()?.setMostRecentSiteInvoiceablesColumnState();
    } catch (error) {
      if (error instanceof Error) {
        this.overlayService.error(error.message);
      }
    } finally {
      this.editMode.set(false);
      this.isDataProcessing.set(false);
    }
  }

  private async saveUpdateGridData() {
    const gridData = this.siteBudgetGrid()?.getGridData() ?? [];
    const initialData = this.siteBudgetGrid()?.initialData ?? [];

    const gridDiff = gridData
      .map((item, index) => {
        if (!isEqual(item, initialData[index])) {
          return { ...item, originalOrder: index };
        }

        return null;
      })
      .filter(Boolean) as (GridRowData & { originalOrder: number })[];

    const gridDataToUpdate = gridDiff.filter((row) => !!row.paymentScheduleId);
    const gridDataToCreate = gridDiff.filter((row) => !row.paymentScheduleId);

    if (this.isSiteInvoiceablesSelected()) {
      await this.saveUpdateSiteInvoiceables(
        <SiteInvoiceableRowData[]>gridDataToCreate,
        <SiteInvoiceableRowData[]>gridDataToUpdate
      );

      this.siteBudgetGrid()?.clearSelection();
    } else {
      const paymentsToUpdate: UpdateSitePaymentScheduleInput[] = gridDataToUpdate.map((row) => ({
        amount: row.amount || 0,
        id: row.paymentScheduleId || '',
        note: row.note || '',
        patient_protocol_id: row.id,
      }));

      const paymentsToCreate: CreateSitePaymentScheduleInput[] = gridDataToCreate.map((row) => ({
        amount: row.amount || 0,
        note: row.note,
        patient_protocol_id: row.id,
        site_budget_version_id: this.selectedBudgetVersion() ?? '',
        site_id: this.selectedSiteId() ?? '',
      }));

      await this.updateSitePayment.mutateAsync({
        paymentsToUpdate,
      });

      await this.createSitePayment.mutateAsync({
        paymentsToCreate,
        siteBudgetVersionId: this.selectedBudgetVersion() ?? '',
        siteId: this.selectedSiteId() ?? '',
      });

      await this.sitePaymentSchedules.refetch();
    }
  }

  async saveUpdateSiteInvoiceables(
    gridDataToCreate: SiteInvoiceableRowData[],
    gridDataToUpdate: SiteInvoiceableRowData[]
  ) {
    const patientProtocolSubTypeId = this.siteBudgetGrid()?.siteInvoiceablesSubTypeId();

    const createInputs = convertToCreateSiteInvoiceableInput(
      gridDataToCreate,
      this.selectedSiteId() ?? '',
      this.selectedBudgetVersion() ?? '',
      patientProtocolSubTypeId ?? '',
      this.selectedProtocolVersion() ?? ''
    );

    const updateInputs = convertToUpdateSiteInvoiceableInput(
      gridDataToUpdate,
      this.selectedSiteId() ?? '',
      patientProtocolSubTypeId ?? '',
      this.selectedProtocolVersion() ?? ''
    );

    await this.injectBatchCreateSiteInvoiceables.mutateAsync({
      inputs: createInputs,
    });

    await this.injectBatchUpdateSiteInvoiceables.mutateAsync({
      inputs: updateInputs,
    });
  }

  // eslint-disable-next-line complexity
  private async setInitialValues(versions?: {
    protocolVersionId: string;
    siteBudgetVersionId?: string;
  }) {
    const firstProtocolVersion = this.protocolVersions.data()?.length
      ? versions?.protocolVersionId || this.protocolVersions.data()![0].id
      : null;

    const siteBudget = this.siteBudget
      .data()
      ?.sort(({ create_date }, { create_date: create_date2 }) =>
        Utils.dateSort(create_date, create_date2)
      )
      .find((budget) => {
        if (versions?.siteBudgetVersionId) {
          return (
            budget.id === versions.siteBudgetVersionId &&
            budget.patient_protocol_version_id === firstProtocolVersion
          );
        }

        return budget.patient_protocol_version_id === firstProtocolVersion;
      });

    if (!siteBudget) return;

    let protocolVersion = firstProtocolVersion ?? null;
    let siteBudgetVersion = siteBudget.id ?? null;

    let patientGroup = this.getInitialPatientGroup(this.injectSitePatientGroups.data()!);

    const params = await firstValueFrom(this.route.queryParams);

    if (params['patient_protocol_version_id']) {
      protocolVersion = params['patient_protocol_version_id'];
    }

    if (params['site_budget_version_id']) {
      siteBudgetVersion = params['site_budget_version_id'];
    }

    if (params['patient_group_id'] && this.isVisitCostSelected()) {
      patientGroup = params['patient_group_id'];
    }

    void this.router.navigate([], { queryParams: {}, replaceUrl: true });

    const note = siteBudget.notes[0];

    this.siteBudgetForm.patchValue({
      effectiveDate: siteBudget?.effective_date ?? null,
      note: note?.message ?? null,
      noteId: note?.id ?? null,
      patientGroup,
      protocolVersion,
      siteBudgetName: siteBudget?.name ?? null,
      siteBudgetVersion,
    });
  }
}
